import { eventHasRun } from '../../utils/eventTracker';
import getPIIExistence from '../../utils/getPIIExistence';
import {
	wsTrackNrClickPathMissing,
	wsTrackNrBlockedFormConfirmation
} from '../../utils/wsTrackNr';
import hasMyWalletInteraction from '../../utils/hasMyWalletInteraction';
import mapPageAlias from '../../utils/mapPageAlias';
import {
	hasStoredFormEventId,
	storeFormEventId
} from '../../utils/storeFormEventId';

export const DDC_FORM_CONFIRMATION = 'ddc.form.confirmation';

const getInputFieldValue = ($form, attrVal, attr = 'name') =>
	$form.find(`[${attr}='${attrVal}']`).val();

async function confirmation(win, doc, event, eventCategory = '') {
	const { DDC } = win;
	let eventObject;
	const { dataLayer } = DDC;
	dataLayer.confirmation = dataLayer.confirmation || {};
	const dataLayerConfirmation = dataLayer.confirmation;
	const $element = event
		? $(event.target)
		: $(dataLayerConfirmation?.element);
	const $form = $element.closest('form');
	const submissionTimestamp =
		$form
			.find(
				'[name="_custom=SUBMISSION_TIMESTAMP=-1=submissionTimestamp"]'
			)
			.val() || dataLayerConfirmation.submissionTimestamp;
	// do not block form confirmation events that have a missing timestamp
	// and
	// only check eventHasRun if there is a timestamp
	// otherwise could prevent tracking on pages with multiple forms that lack submission timestamps
	if (
		!submissionTimestamp ||
		(!!submissionTimestamp &&
			!eventHasRun(DDC_FORM_CONFIRMATION, {
				timeStamp: submissionTimestamp
			}))
	) {
		const formTrackingName =
			getInputFieldValue($form, 'formTrackingName') ||
			dataLayerConfirmation.formTrackingName ||
			$form.data('form-tracking-name') ||
			DDC.getUrlParams().formTrackingName;

		const {
			email,
			emailLowerCase,
			firstName,
			firstNameLowerCase,
			lastName,
			lastNameLowerCase,
			phone,
			street,
			streetLowerCase
		} = dataLayerConfirmation || {};

		let piiData = {
			fields: {},
			fieldsTrimmedLowerCase: {}
		};

		if (email || firstName || lastName || phone || street) {
			piiData = {
				fields: {
					email,
					firstName,
					lastName,
					phone,
					street
				},
				fieldsTrimmedLowerCase: {
					email: emailLowerCase,
					firstName: firstNameLowerCase,
					lastName: lastNameLowerCase,
					phone,
					street: streetLowerCase
				}
			};
		}

		const eventData = {
			currentTime: new Date().getTime(),
			customFormId:
				getInputFieldValue($form, 'custom.form.id') ||
				dataLayerConfirmation.customFormId ||
				'',
			elapsedTime:
				parseInt(
					getInputFieldValue($form, 'dl.formElapsedTime') ||
						dataLayerConfirmation.elapsedTime,
					10
				) || '',
			element:
				getInputFieldValue($form, 'dl.element') ||
				dataLayerConfirmation.element ||
				'',
			eventCategory,
			...(piiData &&
				Object.keys(piiData).length && {
					...piiData
				}),
			formEventId:
				getInputFieldValue($form, 'formEventId') ||
				dataLayerConfirmation.formEventId ||
				'',
			submissionTimestamp,
			formTitle:
				getInputFieldValue($form, 'dl.widgetName') ||
				dataLayerConfirmation.formTitle ||
				'',
			fromVdp:
				getInputFieldValue($form, 'dl.fromVdp') === 'true' ||
				window.DDC?.WidgetData?.['ws-tracking']?.confirmationData?.form
					?.fields?.fromVdp ||
				false,
			fromVlp:
				getInputFieldValue($form, 'dl.fromVlp') === 'true' ||
				window.DDC?.WidgetData?.['ws-tracking']?.confirmationData?.form
					?.fields?.fromVlp ||
				false,
			isModal:
				getInputFieldValue($form, 'dl.isModal') === 'true' ||
				dataLayerConfirmation.isModal === 'true' ||
				false,
			params:
				getInputFieldValue($form, 'dl.params') ||
				dataLayer.page.pageInfo.params ||
				'',
			preferredContact:
				getInputFieldValue($form, 'dl.contact.preferredContact') ||
				dataLayerConfirmation.preferredContact ||
				'',
			pageName:
				getInputFieldValue($form, 'dl.pageName') ||
				DDC.getUrlParams().pageAlias ||
				'',
			pixallPageType: mapPageAlias(dataLayer.confirmation.pageName) || '',
			trackingTags:
				getInputFieldValue($form, 'trackingTags') ||
				dataLayerConfirmation.trackingTags ||
				'',
			trackingId:
				getInputFieldValue($form, 'dl.formTrackingId') ||
				dataLayerConfirmation.trackingId ||
				$form.data('form-tracking-id') ||
				'',
			...(formTrackingName && { formTrackingName }),
			userSessionId:
				dataLayer.confirmation.pageName ||
				getInputFieldValue($form, 'userSessionId') ||
				'',
			validate:
				getInputFieldValue($form, 'dl.validate') === 'true' ||
				dataLayerConfirmation.validate === 'true' ||
				false,
			vehicle:
				dataLayerConfirmation?.vehicle || dataLayer.vehicles?.[0] || '',
			zip:
				getInputFieldValue(
					$form,
					'dl.contact.address.postalCode',
					'data-id'
				) ||
				dataLayerConfirmation.zip ||
				'',
			...getPIIExistence($form[0], DDC)
		};

		const { flags } = win.DDC.WidgetData['ws-tracking'];

		if (
			flags['ws-tracking-block-duplicate-form-event-ids'] &&
			eventData.formEventId &&
			hasStoredFormEventId(eventData.formEventId)
		) {
			wsTrackNrBlockedFormConfirmation(eventData.formEventId);
			return null;
		} else if (
			flags['ws-tracking-block-duplicate-form-event-ids'] &&
			eventData.formEventId
		) {
			storeFormEventId(eventData.formEventId);
		}

		if (
			flags['ws-tracking-block-duplicate-form-session-ids'] &&
			eventData.userSessionId &&
			eventData.userSessionId !== sessionStorage.getItem('userSessionId')
		) {
			wsTrackNrBlockedFormConfirmation(eventData.formEventId);
			return null;
		}

		if (dataLayerConfirmation?.formVehicleModel) {
			eventData.formVehicleModel =
				dataLayerConfirmation?.formVehicleModel ||
				getInputFieldValue($form, 'model');
		}

		const initiatingClickText = localStorage?.getItem(
			'ddc_data_layer-initiatingClickText'
		);
		if (initiatingClickText) {
			eventData.initiatingClickText = initiatingClickText;
			localStorage?.removeItem('ddc_data_layer-initiatingClickText');
		}

		/**
		 * `dataLayer.confirmation` updates for AJAX forms.
		 * Assume confirmation events not fired on POST confirmation page
		 * are AJAX submissions.
		 * NOTE: $pageName defined above is originating page alias and not
		 * current page alias.
		 */
		if (
			DDC?.dataLayer?.page?.pageInfo?.pageName?.indexOf('CONFIRM') === -1
		) {
			// update missing values in confirmation object
			Object.keys(dataLayerConfirmation).forEach((key) => {
				if (
					(typeof dataLayerConfirmation[key] === 'undefined' ||
						dataLayerConfirmation[key] === null) &&
					typeof eventData[key] !== 'undefined' &&
					eventData[key] !== null
				) {
					dataLayerConfirmation[key] = eventData[key];
				}
			});

			// parse vehicle information on non-VDP pages
			const uuid = $form.find("input[name='dl.uuid']").val();
			const vehicle =
				uuid &&
				dataLayer.vehicles.find(
					(vehicleObj) => vehicleObj.uuid === uuid
				);

			if (vehicle) {
				eventData.vehicle = vehicle;
				dataLayerConfirmation.vehicle = vehicle;
				dataLayerConfirmation.uuid = uuid;
			}
		}

		if (
			eventData.eventCategory === 'digital-retailing-cox' ||
			eventData.formEventId ||
			eventData.submissionTimestamp ||
			eventData.trackingId ||
			eventData.customFormId
		) {
			eventObject = {
				eventType: DDC_FORM_CONFIRMATION,
				eventData
			};
		}

		if (eventData.validate === true) {
			// Only when validate is true we want to check if there is a clickpath
			// We want to grab the params and parse the clickpath
			// If the string or the value from the clickpath string does not exist then we log it on newRelic
			const { params } = eventData;
			const paramsArray = params.split('&');
			const clickPathString =
				paramsArray.find((item) => item.includes('clickpath')) || '';
			const [, clickPath] = clickPathString.split('=');

			if (!clickPath) {
				wsTrackNrClickPathMissing();
			}
		}

		eventData.mywallet = !!hasMyWalletInteraction(win);

		if (
			win?.DDC?.WidgetData?.['ws-tracking']?.flags?.[
				'ws-tracking-form-reporting'
			]
		) {
			win?.newrelic?.addPageAction('wsTrackingFormConfirmation', {
				confirmationPageAlias:
					win?.DDC?.dataLayer?.page?.pageInfo?.pageName,
				confirmationPagePath:
					win?.DDC?.dataLayer?.page?.pageInfo?.pagePath,
				customFormId: eventData.customFormId,
				dealershipName: win?.DDC?.dataLayer?.dealership?.dealershipName,
				formEventId: eventData.formEventId,
				formLocation: eventData.isModal
					? 'modal'
					: win?.DDC?.WidgetData?.['ws-tracking']?.confirmationData
							?.form?.fields?.formLocation,
				formPageAlias: eventData.pageName,
				formPagePath: win?.DDC?.dataLayer?.confirmation?.formPagePath,
				formTrackingName: eventData.formTrackingName,
				referrerPageAlias: win?.DDC?.dataLayer?.confirmation?.pageName,
				referrerPagePath: doc?.referrer,
				referrerPixallPageType: eventData.pixallPageType,
				source: 'ws-tracking',
				trackingId: eventData.trackingId
			});
		}
	}

	return eventObject;
}

export default confirmation;
